/**
 * Materiais e texturas procedurais das portas ACM, compartilhados entre a imagem estática
 * (proposta/contrato) e o visualizador 3D interativo: tinta acetinada, metálico com flocos,
 * veio de madeira (cores da cartela Day Brasil) e alumínio escovado.
 */
import { CanvasTexture, Color, MeshPhysicalMaterial, MeshStandardMaterial, MirroredRepeatWrapping, RepeatWrapping, SRGBColorSpace, Texture } from 'three';
import { ACABAMENTO_COR } from './calculoAcm';

const texturas = new Map<string, CanvasTexture>();

/** Textura-base em cache (não deve ser descartada com o material). */
export function ehTexturaBase(t: Texture | null | undefined): boolean {
    return !!t && (Array.from(texturas.values()).includes(t as CanvasTexture) || Array.from(fotos.values()).some(f => f.tex === t));
}

// ------------------------------------------------------------------ fotos das chapas (cartela Day Brasil)

/** Foto da chapa (JPG em /texturas-acm): lado menor ≈ 0,6 m de chapa; o veio corre na vertical da imagem. */
const FOTO_LARGURA_M = 0.6;
const FOTO_ALTURA_M = 1.04;

interface Foto { tex: Texture | null; carregada: Promise<void>; aoCarregar: Array<() => void> }
const fotos = new Map<string, Foto>();

const semNavegador = () => typeof Image === 'undefined' || (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent));

function foto(nome: string): Foto {
    const pronta = fotos.get(nome);
    if (pronta) return pronta;
    const f: Foto = { tex: null, carregada: Promise.resolve(), aoCarregar: [] };
    fotos.set(nome, f);
    if (semNavegador()) return f;
    f.carregada = new Promise<void>(resolve => {
        const img = new Image();
        img.onload = () => {
            const tex = new Texture(img);
            tex.wrapS = tex.wrapT = MirroredRepeatWrapping; // espelha para esconder a emenda entre repetições
            tex.colorSpace = SRGBColorSpace;
            tex.anisotropy = 4;
            tex.needsUpdate = true;
            f.tex = tex;
            f.aoCarregar.splice(0).forEach(cb => cb());
            resolve();
        };
        img.onerror = () => resolve(); // sem a foto, vale a textura procedural
        img.src = `${import.meta.env.BASE_URL}texturas-acm/${nome}.jpg`;
    });
    return f;
}

/**
 * Carrega as fotos das chapas usadas (necessário antes de `imagemPorta3D`, que desenha na hora).
 * Nunca rejeita: sem foto, o render usa a textura procedural de reserva.
 */
export function precarregarTexturasAcm(nomesCor: Array<string | undefined>): Promise<void> {
    const arquivos = new Set(nomesCor.map(n => (n ? ACABAMENTO_COR[n]?.textura : undefined)).filter(Boolean) as string[]);
    return Promise.all(Array.from(arquivos).map(a => foto(a).carregada)).then(() => undefined);
}

/** Peças rebaixadas (interior da cava, fundo do friso): recebem tom mais escuro de sombra de canto. */
export function ehRebaixada(id: string): boolean {
    return (id.startsWith('cava-') && !id.includes('aba')) || id.startsWith('friso-fundo') || id.startsWith('fresa-') || id.startsWith('ripado-fundo');
}

// ------------------------------------------------------------------ texturas procedurais

function rng(seed: number) {
    let s = seed >>> 0;
    return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
}

function canvasTextura(chave: string, tam: number, desenhar: (g: CanvasRenderingContext2D, t: number) => void, cor = true): CanvasTexture {
    const pronta = texturas.get(chave);
    if (pronta) return pronta;
    const c = document.createElement('canvas');
    c.width = c.height = tam;
    const g = c.getContext('2d')!;
    desenhar(g, tam);
    const tex = new CanvasTexture(c);
    tex.wrapS = tex.wrapT = RepeatWrapping;
    if (cor) tex.colorSpace = SRGBColorSpace;
    tex.anisotropy = 4;
    texturas.set(chave, tex);
    return tex;
}

/** Veio de madeira vertical (as bandejas correm na altura da porta). */
export function texturaMadeira(veios: [string, string, string]) {
    return canvasTextura(`madeira:${veios.join()}`, 1024, (g, t) => {
        const r = rng(7);
        g.fillStyle = veios[1];
        g.fillRect(0, 0, t, t);
        // Faixas largas (variação de tom entre lâminas)
        for (let i = 0; i < 18; i++) {
            const x0 = r() * t;
            const larg = 20 + r() * 90;
            const grad = g.createLinearGradient(x0 - larg, 0, x0 + larg, 0);
            const cor = r() < 0.5 ? veios[0] : veios[2];
            grad.addColorStop(0, 'transparent');
            grad.addColorStop(0.5, cor);
            grad.addColorStop(1, 'transparent');
            g.globalAlpha = 0.25 + r() * 0.3;
            g.fillStyle = grad;
            g.fillRect(x0 - larg, 0, 2 * larg, t);
        }
        // Veios ondulados com "catedrais" (arcos alongados) e fibras finas
        for (let i = 0; i < 260; i++) {
            const x0 = r() * t;
            const amp = 3 + r() * 14;
            const freq = 0.5 + r() * 1.8;
            const fase = r() * 10;
            g.strokeStyle = r() < 0.65 ? veios[0] : veios[2];
            g.globalAlpha = 0.12 + r() * 0.35;
            g.lineWidth = 0.4 + r() * (r() < 0.15 ? 4 : 1.6);
            g.beginPath();
            for (let y = 0; y <= t; y += 6) {
                const x = x0 + Math.sin((y / t) * Math.PI * 2 * freq + fase) * amp + Math.sin(y * 0.05 + i) * 0.8;
                if (y === 0) g.moveTo(x, y); else g.lineTo(x, y);
            }
            g.stroke();
        }
        g.globalAlpha = 1;
    });
}

/** Tom mais escuro para faces rebaixadas (interior da cava, fundo do friso): simula sombra de canto. */
export function escurecer(hex: string, fator: number) {
    const c = new Color(hex);
    c.multiplyScalar(fator);
    return `#${c.getHexString()}`;
}

/** Flocos metálicos finos (mapa de rugosidade: pontos lisos brilham). */
export function texturaFlocos() {
    return canvasTextura('flocos', 256, (g, t) => {
        const r = rng(3);
        g.fillStyle = 'rgb(150,150,150)';
        g.fillRect(0, 0, t, t);
        for (let i = 0; i < 9000; i++) {
            const v = Math.floor(40 + r() * 190);
            g.fillStyle = `rgb(${v},${v},${v})`;
            g.fillRect(r() * t, r() * t, 1, 1);
        }
    }, false);
}

/** Casca acetinada leve para tinta sólida (quebra o aspecto plástico). */
export function texturaAcetinado() {
    return canvasTextura('acetinado', 256, (g, t) => {
        const r = rng(11);
        g.fillStyle = 'rgb(120,120,120)';
        g.fillRect(0, 0, t, t);
        for (let i = 0; i < 5000; i++) {
            const v = Math.floor(100 + r() * 45);
            g.fillStyle = `rgb(${v},${v},${v})`;
            g.fillRect(r() * t, r() * t, 2, 2);
        }
    }, false);
}

/** Alumínio escovado (riscos no sentido do perfil). */
export function texturaEscovado() {
    return canvasTextura('escovado', 256, (g, t) => {
        const r = rng(5);
        g.fillStyle = 'rgb(110,110,110)';
        g.fillRect(0, 0, t, t);
        for (let i = 0; i < 700; i++) {
            const v = Math.floor(70 + r() * 110);
            g.strokeStyle = `rgb(${v},${v},${v})`;
            g.globalAlpha = 0.5;
            g.lineWidth = 0.5 + r();
            const x = r() * t;
            g.beginPath(); g.moveTo(x, 0); g.lineTo(x + (r() - 0.5) * 3, t); g.stroke();
        }
        g.globalAlpha = 1;
    }, false);
}

export function texturaParede() {
    return canvasTextura('parede', 256, (g, t) => {
        const r = rng(21);
        g.fillStyle = '#e2ddd5';
        g.fillRect(0, 0, t, t);
        for (let i = 0; i < 6000; i++) {
            const v = Math.floor(214 + r() * 18);
            g.fillStyle = `rgb(${v},${v - 3},${v - 8})`;
            g.fillRect(r() * t, r() * t, 2, 2);
        }
    });
}

export function texturaPiso() {
    return canvasTextura('piso', 512, (g, t) => {
        const r = rng(31);
        g.fillStyle = '#cdc6bb';
        g.fillRect(0, 0, t, t);
        for (let i = 0; i < 12000; i++) {
            const v = Math.floor(190 + r() * 30);
            g.fillStyle = `rgb(${v},${v - 6},${v - 14})`;
            g.fillRect(r() * t, r() * t, 2, 2);
        }
        g.strokeStyle = '#a79f93';
        g.lineWidth = 3;
        g.strokeRect(0, 0, t, t); // rejunte (porcelanato 60×60 por repetição)
    });
}

// ------------------------------------------------------------------ materiais

export function materialAcm(corNome: string, corHex: string, larguraM: number, alturaM: number, rebaixada = false) {
    const acab = ACABAMENTO_COR[corNome];
    if (rebaixada) corHex = escurecer(corHex, 0.45);
    if (acab?.textura) {
        const madeira = acab.tipo === 'madeira';
        const mat = new MeshPhysicalMaterial(madeira
            ? { color: new Color(rebaixada ? '#666666' : '#e6e6e6'), roughness: 0.62, clearcoat: 0.25, clearcoatRoughness: 0.45 }
            : { color: new Color(rebaixada ? '#666666' : '#ffffff'), roughness: 0.72, metalness: 0.08, clearcoat: 0.1, clearcoatRoughness: 0.6 });
        const f = foto(acab.textura);
        const aplicar = () => {
            const map = f.tex!.clone();
            map.needsUpdate = true;
            map.repeat.set(Math.max(larguraM / FOTO_LARGURA_M, 0.1), Math.max(alturaM / FOTO_ALTURA_M, 0.1));
            mat.map = map;
            mat.needsUpdate = true;
        };
        if (f.tex) { aplicar(); return mat; }
        // Foto ainda carregando: tom da paleta (ou veio procedural) até ela chegar
        if (madeira && acab.veios) {
            const reserva = texturaMadeira(acab.veios).clone();
            reserva.needsUpdate = true;
            reserva.repeat.set(Math.max(larguraM / 1.2, 0.1), Math.max(alturaM / 5, 0.1));
            mat.map = reserva;
        } else {
            mat.color = new Color(corHex);
        }
        f.aoCarregar.push(() => { if (mat.map && !ehTexturaBase(mat.map)) mat.map.dispose(); aplicar(); });
        return mat;
    }
    if (acab?.tipo === 'madeira' && acab.veios) {
        const map = texturaMadeira(acab.veios).clone();
        map.needsUpdate = true;
        map.repeat.set(Math.max(larguraM / 1.2, 0.1), Math.max(alturaM / 5, 0.1));
        return new MeshPhysicalMaterial({ map, color: new Color(rebaixada ? '#666666' : '#d9d9d9'), roughness: 0.62, clearcoat: 0.25, clearcoatRoughness: 0.45 });
    }
    if (acab?.tipo === 'metalico') {
        const rough = texturaFlocos().clone();
        rough.needsUpdate = true;
        rough.repeat.set(larguraM * 8, alturaM * 8);
        return new MeshPhysicalMaterial({ color: new Color(corHex), metalness: 0.55, roughness: 0.42, roughnessMap: rough, clearcoat: 0.6, clearcoatRoughness: 0.25 });
    }
    if (acab?.brilho) {
        // Chapa brilhante: tinta lisa com verniz espelhado (sem a casca acetinada)
        return new MeshPhysicalMaterial({ color: new Color(corHex), metalness: 0.05, roughness: 0.16, clearcoat: 1, clearcoatRoughness: 0.06 });
    }
    const rough = texturaAcetinado().clone();
    rough.needsUpdate = true;
    rough.repeat.set(larguraM * 4, alturaM * 4);
    return new MeshPhysicalMaterial({ color: new Color(corHex), metalness: 0.05, roughness: 0.5, roughnessMap: rough, clearcoat: 0.45, clearcoatRoughness: 0.35 });
}

/** Ferragens: kit Preto (fosco metálico) ou Escovado (inox); ferragem oculta; fundo escuro da concha. */
export function materialFerragem(acabamento: 'kit' | 'oculto' | 'recesso', corKit?: string) {
    if (acabamento === 'recesso') return new MeshStandardMaterial({ color: new Color('#0c0c0e'), metalness: 0.2, roughness: 0.85 });
    if (acabamento === 'oculto') return new MeshStandardMaterial({ color: new Color('#2b2b30'), metalness: 0.8, roughness: 0.3 });
    if (corKit === 'Escovado') {
        const rough = texturaEscovado().clone();
        rough.needsUpdate = true;
        // Metal parcial: peças pequenas com metalness 1 refletem só o ambiente e saem escuras
        return new MeshStandardMaterial({ color: new Color('#d3d6db'), metalness: 0.6, roughness: 0.3, roughnessMap: rough });
    }
    return new MeshStandardMaterial({ color: new Color('#1c1c1f'), metalness: 0.65, roughness: 0.38 });
}

export function materialAluminio() {
    const rough = texturaEscovado().clone();
    rough.needsUpdate = true;
    return new MeshStandardMaterial({ color: new Color('#c9ccd2'), metalness: 0.85, roughness: 0.38, roughnessMap: rough });
}

